import * as Yup from 'yup'
import FormikTextField from '@ui-components-3/compat/lib/components/FormikTextField'
import PasswordInput from '@ui-components-3/ui/lib/components/PasswordInput'

import { Formik, FormikHelpers } from 'formik'
import TestIds from 'util/TestIds'
import useDocumentTitle from 'hooks/useDocumentTitle'
import EntryFormCard from '../EntryFormCard'
import { PwValues } from 'pages/EntryPage/EntryPage'

const PasswordSchema = Yup.object().shape({
  password: Yup.string()
    .label('Password')
    .matches(
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{10,}$/g,
      'Password must have 10 characters, and must contain 1 capital letter,' +
        ' 1 lowercase letter, 1 special character, and 1 number',
    )
    .required(),
})

const PASSWORD_VISIBILITY_TOGGLER_PROPS = {
  'data-testid': TestIds.login.button.togglePasswordVisibility,
  tabIndex: 2,
}

export type LoginPwSceneProps = {
  onSubmit: (values: PwValues, form: FormikHelpers<PwValues>) => void
  onForgotPassword: () => void
  onBack: () => void
}

const LoginPasswordScene = (props: LoginPwSceneProps): JSX.Element => {
  useDocumentTitle('Login Password')
  return (
    <EntryFormCard prompt={'Please enter your password'} data-testid={TestIds.login.view.password}>
      <p className="typography-body self-start pb-4 text-start text-neutral-600">
        All fields marked with asterisks are required*
      </p>
      <Formik initialValues={{ password: '' }} validationSchema={PasswordSchema} onSubmit={props.onSubmit}>
        {(renderProps) => {
          const { dirty, isValid, handleSubmit } = renderProps
          return (
            <form onSubmit={handleSubmit} className="flex flex-col items-center">
              <FormikTextField
                fullWidth
                data-testid={TestIds.login.input.password}
                InputComponent={PasswordInput}
                VisibilityTogglerProps={PASSWORD_VISIBILITY_TOGGLER_PROPS}
                schema={PasswordSchema}
                label="Password"
                name="password"
                autoComplete="current-password"
                className="order-2"
                // eslint-disable-next-line jsx-a11y/tabindex-no-positive
                tabIndex={1}
              />

              <button
                type="button"
                className="typography-body text-primary order-1 -mb-2 cursor-pointer self-end font-semibold hover:underline"
                data-testid={TestIds.login.button.forgot}
                onClick={props.onForgotPassword}
                // eslint-disable-next-line jsx-a11y/tabindex-no-positive
                tabIndex={3}
              >
                Forgot Password?
              </button>

              <div className="gap-s order-3 mt-9 flex w-full items-center justify-between">
                <button
                  className="btn btn-neutral-outlined grow"
                  data-testid={TestIds.login.button.cancel}
                  onClick={props.onBack}
                  type="button"
                  // eslint-disable-next-line jsx-a11y/tabindex-no-positive
                  tabIndex={4}
                >
                  Back
                </button>
                <button
                  className="btn btn-primary grow"
                  data-testid={TestIds.login.button.login}
                  disabled={!dirty || !isValid}
                  type="submit"
                  // eslint-disable-next-line jsx-a11y/tabindex-no-positive
                  tabIndex={5}
                >
                  Continue
                </button>
              </div>
            </form>
          )
        }}
      </Formik>
    </EntryFormCard>
  )
}

export default LoginPasswordScene
